/**
 * THE FIND PAGE (ops #84, `docs/plan-client-workplace.md` section 6): `/find?number=<E.164>` and
 * `/find?lead=<lead id>`, the address the phone system's wall (faithfully-kept-phone#31) and the
 * website's lead wall (site-faithfully-kept#30) open in a new tab. It asks the find door once and
 * goes: a household to its page, an enquiry to the Enquiries screen with that row open, several
 * households to a short chooser, and nobody to a calm page with a way to look again.
 *
 * OFFICE ONLY. The route is in the office branch of `App.tsx` alone, and the door refuses a cleaner
 * on the server, which is the control. A person signed out meets the sign-in and comes back to this
 * same address, query and all (`SignedOut.tsx`).
 *
 * NOBODY YET IS NOT AN ERROR. The wall opens the tab the moment the call rings, and the phone
 * system's `call.started` fact, which opens the enquiry for a number ops has never seen, may land a
 * moment after. So the page says there is no record yet and offers to try again; it never creates
 * anything, since ops never mints a lead or an enquiry from a screen (seam rule 2).
 *
 * THE NUMBER IS NAMED BY ITS LAST FOUR DIGITS until a record is found. It travels in the address
 * alone, and every landing replaces this address in the tab's history, so Back does not hold it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, findRecord, type Found } from '../api.ts';
import { Card, MEASURE, Pill, ScreenTitle } from '../components/AppShell.tsx';
import { Button } from '../components/Field.tsx';
import { Busy, EmptyNotice, ErrorNotice } from '../components/Notice.tsx';

/**
 * The number as the address carries it. A bare `+` in an address is read as a space, so a space and
 * digits are the plus they were; anything else is taken as it came, and the door says if it is not
 * a number.
 */
export function numberAsked(raw: string): string {
  const trimmed = raw.trim();
  return /^ \d/.test(raw) && /^\d+$/.test(trimmed) ? `+${trimmed}` : trimmed;
}

/** A number named without saying it: the area code and the last four of a North American one. */
export function numberHint(number: string): string {
  const american = /^\+1(\d{3})\d{3}(\d{4})$/.exec(number);
  if (american) return `(${american[1]}) ...-${american[2]}`;
  const digits = number.replace(/\D/g, '');
  return digits.length >= 4 ? `...${digits.slice(-4)}` : 'this number';
}

/** The household's standing, beside its name in the chooser. */
const STATUS_WORDS: Record<string, string> = {
  active: 'Active',
  paused: 'Paused',
  ended: 'Ended',
};

type Asked = { readonly number: string } | { readonly lead: string } | null;

type State =
  | { readonly kind: 'asking' }
  | { readonly kind: 'answered'; readonly found: Found }
  | { readonly kind: 'refused'; readonly error: ApiError };

export function Find() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: 'asking' });
  const [attempt, setAttempt] = useState(0);

  const rawNumber = search.get('number');
  const rawLead = search.get('lead');
  const asked: Asked = useMemo(() => {
    if (rawNumber !== null && rawNumber.trim() !== '') return { number: numberAsked(rawNumber) };
    if (rawLead !== null && rawLead.trim() !== '') return { lead: rawLead.trim() };
    return null;
  }, [rawNumber, rawLead]);

  const again = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (asked === null) return;
    let live = true;
    setState({ kind: 'asking' });
    findRecord(asked)
      .then((found) => {
        if (!live) return;
        // Every landing replaces this address, so the number never stays in the tab's history.
        if (found.kind === 'client') {
          navigate(`/clients/${encodeURIComponent(found.clientId)}`, { replace: true });
        } else if (found.kind === 'enquiry') {
          navigate(`/enquiries?open=${encodeURIComponent(found.enquiryId)}`, { replace: true });
        } else {
          setState({ kind: 'answered', found });
        }
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setState({
          kind: 'refused',
          error:
            thrown instanceof ApiError
              ? thrown
              : new ApiError(0, 'error', 'The record could not be looked for.'),
        });
      });
    return () => {
      live = false;
    };
  }, [asked, attempt, navigate]);

  const what = asked === null ? null : 'number' in asked ? numberHint(asked.number) : 'this lead';

  return (
    <div className={MEASURE}>
      <ScreenTitle title="Find" subtitle="The record behind a call or a website lead." />
      {asked === null ? (
        <EmptyNotice title="Nothing to find">
          <p>
            This page is opened from the call wall or the lead wall, with a number or a lead in its
            address. Search the client book instead.
          </p>
          <Link
            to="/clients"
            className="mt-2 inline-flex min-h-11 items-center underline desk:min-h-9"
          >
            Open the clients
          </Link>
        </EmptyNotice>
      ) : state.kind === 'refused' ? (
        <ErrorNotice
          title="The record could not be looked for"
          message={state.error.message}
          onRetry={again}
        />
      ) : state.kind === 'asking' ? (
        <Busy what={`the record for ${what}`} />
      ) : state.found.kind === 'choose' ? (
        <Card label="Households holding this number">
          <h2 className="font-serif text-lg">
            {state.found.households.length} households hold {what}
          </h2>
          <p className="mt-1 text-sm text-navy/70">
            Choose the one this call is about. The newest person to hold the number is first.
          </p>
          <ul className="mt-3 space-y-2">
            {state.found.households.map((one) => (
              <li key={one.clientId}>
                <Link
                  to={`/clients/${encodeURIComponent(one.clientId)}`}
                  replace
                  className="flex min-h-12 flex-wrap items-center gap-2 rounded-lg border border-navy/15 bg-cream px-3 py-2 desk:min-h-10"
                >
                  <span className="font-medium">{one.name}</span>
                  <span className="text-sm text-navy/70">{one.person}</span>
                  {one.status !== 'active' ? (
                    <Pill>{STATUS_WORDS[one.status] ?? one.status}</Pill>
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      ) : (
        <Card label="No record yet">
          <h2 className="font-serif text-lg">No record yet for {what}</h2>
          <p className="mt-1 text-sm text-navy/75">
            {'number' in asked
              ? 'A call from a new number opens an enquiry a moment after it rings, so the record may not be here yet. Try again in a few seconds.'
              : 'A lead from the website reaches ops a moment after the form is sent, so the record may not be here yet. Try again in a few seconds.'}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button onClick={again}>Try again</Button>
            <Link
              to="/enquiries"
              className="inline-flex min-h-11 items-center underline desk:min-h-9"
            >
              Open the enquiries
            </Link>
          </div>
        </Card>
      )}
    </div>
  );
}
